import PropTypes from 'prop-types'
import R from 'ramda'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useMutation } from 'react-query'

import FourFlashcards from 'Images/four_flashcards.svg'
import {
  Box,
  Button,
  CheckboxControl,
  Columns,
  Col,
  Dialog,
  Icon,
  Heading,
  HR,
  Image,
  LoadingPlane,
  LoadingSpinner,
  MonoButton,
  SimpleInput,
  Text,
  useToast,
} from '@redriverpress/system'

import { capitalCase } from 'text-case'
import services from '../services/flashcardSets'
import useFlashcardSets from '../hooks/useFlashcardSets'

export const SAVE_MODES = {
  copy: 'copy',
  save: 'save',
  paste: 'paste',
}

const translations = {
  [SAVE_MODES.copy]: {
    past: 'copied',
    progressive: 'copying',
  },
  [SAVE_MODES.save]: {
    past: 'saved',
    progressive: 'saving',
  },
  [SAVE_MODES.paste]: {
    past: 'pasted',
    progressive: 'pasting',
  },
}

/* This component borrows several patterns from MoveItemModal.js.
 * Changes to one component may need to be reflected in the other.
 */
export default function SaveToSetsDialog({
  flashcards,
  onClose,
  onOpenChange,
  onSaved,
  open,
  mode,
  exclude,
}) {
  const translation = translations[mode] || translations[SAVE_MODES.save]

  const { addToast } = useToast()

  const {
    isLoading: flashcardSetsLoading,
    data,
    refetch: refetchFlashcardSets,
  } = useFlashcardSets({ exclude })

  const [newSets, setNewSets] = useState([])
  const addNewSet = newSet => setNewSets(sets => R.prepend(newSet, sets))

  const [namingNewSet, setNamingNewSet] = useState(false)
  const newSetInput = useRef(null)

  const createSetMutation = useMutation(services.createFlashcardSet, {
    onSuccess: ({ data: newFlashcardSet }) => {
      addNewSet(newFlashcardSet)
      setNamingNewSet(true)
      newSetInput.current.focus()
    },
  })

  const renameSetMutation = useMutation(
    // Let's only update the name, keep existing description.
    ({ id, name }) => services.updateFlashcardSet({ id, name }),
    {
      onSuccess: ({ data: renamedSet }) => {
        setNamingNewSet(false)
        setNewSets(current =>
          R.adjust(0, R.assoc('name', renamedSet.name), current)
        )
      },
    }
  )

  // Yes, this naming is ridiculous. I know. :)
  const [setsSelection, setSetsSelection] = useState([])
  const toggleSetSelection = setId =>
    setSetsSelection(current =>
      current.includes(setId)
        ? R.without([setId], current)
        : R.append(setId, current)
    )

  const [savePending, setSavePending] = useState(false)
  const [saveErrored, setSaveErrored] = useState(false)

  const handleSaveFlashcards = async () => {
    setSavePending(true)
    setSaveErrored(false)
    try {
      await services.createUserFlashcards({
        setIds: setsSelection,
        flashcards,
      })
      setSavePending(false)
      addToast({
        id: `${capitalCase(translation.past)} Flashcards`,
        content: `Successfully ${translation.past} ${
          flashcards.length
        } flashcard${flashcards.length !== 1 ? 's' : ''}`,
        timeout: 3000,
        action: mode === SAVE_MODES.save && (
          <MonoButton small as='a' mr={-2} href='/flashcard_sets'>
            <Icon icon='images' pr={2} /> View Sets
          </MonoButton>
        ),
      })
      onSaved()
      onClose()
    } catch {
      setSavePending(false)
      setSaveErrored(true)
    }
  }

  useEffect(() => {
    // When closing the modal, reset newSets and setsSelection,
    // and refetch flashcard sets (in case user created new sets while dialog was open)
    if (!open) {
      setNewSets([])
      setSetsSelection([])
      refetchFlashcardSets()
    }
  }, [open])

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.StyledContent>
          <Dialog.StyledTitle>
            <Heading as='h6' mb={0}>
              {capitalCase(mode)} to…
            </Heading>
          </Dialog.StyledTitle>

          {flashcardSetsLoading && (
            <Box display='flex' justifyContent='center' py={3}>
              <LoadingPlane />
            </Box>
          )}

          {!flashcardSetsLoading &&
          (data.flashcardSets?.length || newSets?.length) ? (
            <>
              <Text mt={[0, null, 4]}>
                {flashcards.length === 1 ? (
                  <>
                    Where would you like to {mode}{' '}
                    <strong>{flashcards[0].title}</strong>?
                  </>
                ) : (
                  <>Where would you like to {mode} your selected flashcards?</>
                )}
              </Text>
              <Box
                my={[3, null, 4]}
                maxHeight={['12rem', null, '15rem']}
                overflow='scroll'
                borderRadius='rounded'
                border='2px solid'
                borderColor='gray150'
              >
                {newSets &&
                  newSets.map(set => (
                    <Fragment key={set.id}>
                      <Box p={2} display='flex' alignItems='center'>
                        <Box as='label' flexShrink='0'>
                          <CheckboxControl
                            adaptive
                            name='newFlashcardSets'
                            value={set.id}
                            checked={setsSelection.includes(set.id)}
                            onChange={() => toggleSetSelection(set.id)}
                          />
                        </Box>
                        {set === newSets[0] && namingNewSet ? (
                          <SimpleInput
                            ml={2}
                            defaultValue={set.name}
                            ref={newSetInput}
                            autoComplete='off'
                            onFocus={e => e.target.select()}
                            onKeyDown={e =>
                              e.keyCode === 13 &&
                              renameSetMutation.mutate({
                                id: set.id,
                                name: e.target.value,
                              })
                            }
                            onBlur={e =>
                              renameSetMutation.mutate({
                                id: set.id,
                                name: e.target.value,
                              })
                            }
                          />
                        ) : (
                          <Text trunclipse pl={2}>
                            {set.name}
                          </Text>
                        )}
                      </Box>
                      {/* Don't render an HR under this set if it's the last one in the newSets
                      and there are no existing sets (results in a double bottom border w/ wrapping box) */}
                      {!data.flashcardSets?.length &&
                      R.equals(set, R.last(newSets)) ? null : (
                        <HR borderColor='gray150' />
                      )}
                    </Fragment>
                  ))}
                {data.flashcardSets &&
                  data.flashcardSets.map(set => (
                    <Fragment key={set.id}>
                      <Box p={2} display='flex' alignItems='center'>
                        <Box as='label' flexShrink='0'>
                          <CheckboxControl
                            adaptive
                            name='flashcardSets'
                            value={set.id}
                            checked={setsSelection.includes(set.id)}
                            onChange={() => toggleSetSelection(set.id)}
                          />
                        </Box>
                        <Text trunclipse pl={2}>
                          {set.name}
                        </Text>
                      </Box>
                      {R.equals(set, R.last(data.flashcardSets)) ? null : (
                        <HR borderColor='gray150' />
                      )}
                    </Fragment>
                  ))}
              </Box>
            </>
          ) : (
            <Box textAlign='center'>
              <Image
                src={FourFlashcards}
                width='240px'
                height='auto'
                my={4}
                alt=''
              />
              <Heading as='h5' mb={4}>
                You have no flashcard sets
              </Heading>
            </Box>
          )}

          <Columns>
            {saveErrored && (
              <Col width={1} mb={3}>
                <Box
                  p={3}
                  bg='red50'
                  borderRadius='rounded'
                  display='flex'
                  alignItems='baseline'
                >
                  <Icon icon='exclamation-triangle' pr={3} />
                  <Text fontSize={0} flex={1}>
                    Something went wrong when {translation.progressive} your
                    flashcards. Please&nbsp;try&nbsp;again!
                  </Text>
                </Box>
              </Col>
            )}

            <Col width={[1, null, 'auto']} mb={[3, null, 0]}>
              <Button
                adaptive
                secondary
                width={[1, null, 'auto']}
                onClick={createSetMutation.mutate}
                disabled={createSetMutation.isLoading || namingNewSet}
              >
                {createSetMutation.isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <Icon icon='plus' />
                )}
                <Text as='span' pl={2}>
                  New Set
                </Text>
              </Button>
            </Col>
            <Col width={[1 / 2, null, 'auto']} ml={[null, null, 'auto']}>
              <Button
                adaptive
                secondary
                width={[1, null, 'auto']}
                onClick={onClose}
                disabled={namingNewSet}
              >
                <Icon icon='times' pr={2} />
                Cancel
              </Button>
            </Col>
            <Col width={[1 / 2, null, 'auto']}>
              <Button
                adaptive
                width={[1, null, 'auto']}
                onClick={handleSaveFlashcards}
                disabled={
                  setsSelection.length === 0 || savePending || namingNewSet
                }
              >
                {savePending ? <LoadingSpinner /> : <Icon icon='check' />}
                <Text as='span' pl={2}>
                  {capitalCase(mode)}
                </Text>
              </Button>
            </Col>
          </Columns>
        </Dialog.StyledContent>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

SaveToSetsDialog.defaultProps = {
  onSaved: () => {},
  mode: SAVE_MODES.save,
  exclude: [],
}

SaveToSetsDialog.propTypes = {
  flashcards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string,
      userTitle: PropTypes.string,
    })
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  mode: PropTypes.oneOf(Object.values(SAVE_MODES)),
  exclude: PropTypes.arrayOf(PropTypes.number),
}
